<template>
    <BaseInput
        :model-value="modelValue"
        v-bind="$attrs"
        :label="label"
        :name="name || label">
        <div
            v-if="!smallLayout"
            class="col-span-full">
            <div
                class="flex flex-wrap items-center justify-start gap-2">
                <template v-if="modelValue && modelValue.length">
                    <div
                        v-for="document in modelValue"
                        :key="'document-' + document.id"
                        class="w-32">
                        <div
                            class="w-32 h-32 rounded relative"
                            :class="{
                                'bg-primary':!disabled && !(document.url && document.mime_type.startsWith('image/')),
                                'bg-neutral-500':disabled && !(document.url && document.mime_type.startsWith('image/')),
                            }">
                            <img
                                v-if="document.url && document.mime_type.startsWith('image/')"
                                :src="document.url"
                                :alt="document.file_name"
                                class="w-full h-full object-cover">

                            <Icon
                                v-else
                                :name="fileIcons.getIcon(document)"
                                class="w-full h-full text-white dark:text-gray-800" />

                            <div
                                v-if="!disabled"
                                class="absolute top-0 right-0 pr-2 pt-2">
                                <button
                                    type="button"
                                    title="Entfernen"
                                    class="rounded bg-neutral-600/50 hover:bg-danger-500 text-white focus:outline-none focus:ring-2 focus:ring-danger-500 focus:ring-offset-2"
                                    @click="removeFile(document)">
                                    <span class="sr-only">Close</span>
                                    <Icon
                                        name="heroicons:trash"
                                        class="h-6 w-6 p-1"
                                        aria-hidden="true" />
                                </button>
                            </div>
                        </div>
                        <div class="w-100 truncate">
                            <a
                                href="#"
                                @click.prevent="downloadFile(document)">
                                <small class="underline">{{ document.file_name }}</small>
                            </a>
                        </div>
                    </div>
                </template>

                <template v-if="files && files.length">
                    <div
                        v-for="(_, index) in files"
                        :key="'uploading-file-' + index"
                        class="animate-pulse">
                        <div class="w-32 h-4 bg-neutral-300 rounded-full" />
                    </div>
                </template>
            </div>
            <div
                v-if="showDropzone"
                id="drop_zone"
                class="flex justify-center rounded-lg border border-dashed border-gray-900/25 dark:border-gray-200/25 px-6 py-10 drag"
                :class="(dragging ? 'bg-gray-200 ' : ' ') + dropzoneClass">
                <div
                    class="text-center"
                    @drop.prevent="dropFiles"
                    @dragenter.prevent="dragging = true"
                    @dragover.prevent="dragging = true"
                    @dragleave.prevent="dragging = false">
                    <Icon
                        :name="uploadIcon"
                        :class="'h-8 w-8 ' + uploadIconClass" />
                    <div class="mt-4 flex flex-col text-sm leading-6 text-gray-600 dark:text-gray-300">
                        <div class="flex">
                            <label
                                :for="(inputId === '') ? 'file-upload' : inputId"
                                class="relative cursor-pointer rounded-md bg-transparent dark:bg-gray-700 font-semibold focus-within:outline-none focus-within:ring-2 focus-within:ring-primary-600 focus-within:ring-offset-2 dark:ring-offset-gray-700"
                                :class="{
                                    'text-neutral-500 dark:text-neutral-500': disabled,
                                    'text-primary-600 dark:text-primary hover:text-primary-500': !disabled,
                                }">
                                <span>{{ $t('files.upload') }}</span>
                                <input
                                    :id="(inputId === '') ? 'file-upload' : inputId"
                                    :name="(inputId === '') ? 'file-upload' : inputId"
                                    type="file"
                                    :disabled="disabled"
                                    :accept="accept"
                                    class="sr-only"
                                    :multiple="multiple"
                                    @change="inputFiles">
                            </label>
                            <p class="pl-1">
                                {{ $t('files.orDragAndDrop') }}
                            </p>
                        </div>
                        <div class="text-xs text-gray-400">
                            max. 10 MB
                        </div>
                        <div
                            v-if="computedFormatsLabel"
                            class="mt-2">
                            <div
                                class="inline-flex justify-center items-center gap-1 mt-0.5 text-xs font-medium text-gray-500 bg-gray-100 rounded px-1.5 py-0.5 w-fit">
                                <Icon
                                    name="heroicons:document-check"
                                    class="h-3.5 w-3.5 shrink-0" />
                                {{ computedFormatsLabel }}
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>

        <BaseDropzoneSmallLayout
            v-else
            class="my-2"
            :input-id="inputId"
            :dragging="dragging"
            :disabled="disabled"
            :upload-icon="uploadIcon"
            :accept="accept"
            :multiple="multiple"
            :upload-icon-class="uploadIconClass"
            :dropzone-class="dropzoneClass"
            :accepted-formats-label="computedFormatsLabel"
            @input-files="inputFiles"
            @drop-files="dropFiles"
            @update:dragging="dragging = $event" />
    </BaseInput>
</template>

<script setup>
import BaseDropzoneSmallLayout from "@/components/global/Inputs/BaseDropzone/BaseDropzoneSmallLayout.vue"
import {useNotification} from '@/composables/useNotification'
import {fileIcons} from "@/utils/fileIcons"

const props = defineProps({
    inputId: {
        type: String,
        default: ''
    },
    modelValue: {
        type: Array,
        required: true
    },
    visibility: {
        type: String,
        default: 'local'
    },
    accept: {
        type: String,
        default: ''
    },
    multiple: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    label: {
        type: String,
        default: '',
        description: 'The label text'
    },
    name: {
        type: String,
        default: 'Dropzone',
        description: 'The name of the select (required for validation if no label is provided)'
    },
    folder: {
        type: String,
        default: ''
    },
    uploadIcon: {
        type: String,
        default: 'heroicons:camera'
    },
    smallLayout: {
        type: Boolean,
        default: false
    },
    uploadIconClass: {
        type: String,
        default: ''
    },
    dropzoneClass: {
        type: String,
        default: ''
    },
    organisationId: {
        type: Number,
        default: null,
        description: 'If the document is related to another organisation than the current one'
    }
})

const emits = defineEmits(['update:modelValue'])

const {$apiRoute} = useNuxtApp()
const notification = useNotification()
const {t: $t} = useI18n()

const MIME_LABELS = {
    'application/pdf': 'PDF',
    'image/jpeg': 'JPG',
    'image/jpg': 'JPG',
    'image/png': 'PNG',
    'image/gif': 'GIF',
    'image/webp': 'WebP',
    'image/svg+xml': 'SVG',
    'image/*': 'JPG, PNG',
}

const computedFormatsLabel = computed(() => {
    if (!props.accept) return ''

    const types = props.accept.split(/[,|]/).map(t => t.trim()).filter(Boolean)
    const labels = [...new Set(types.map(t => MIME_LABELS[t]).filter(Boolean))]

    return labels.length ? $t('files.acceptedFormats', {formats: labels.join(', ')}) : ''
})

const files = ref([])
const dragging = ref(false)

const localDocuments = ref([])

const showDropzone = computed(() => {
    return props.multiple || !props.modelValue?.length
})

watchEffect(() => {
    localDocuments.value = [...(props.modelValue ?? [])]
})

function downloadFile(document) {
    if (document.url)
        window.open(document.url, '_blank')
    else
        window.open($apiRoute('spa.documents.show', {document: document.id}), "_blank")
}

function removeFile(document) {
    if (props.disabled) return
    localDocuments.value = localDocuments.value.filter(doc => doc.id !== document.id)
    emits('update:modelValue', [...localDocuments.value])
}

function dropFiles(event) {
    if (props.disabled) return
    event.preventDefault()
    dragging.value = false
    if (!event.dataTransfer?.files?.length) return
    const actualFiles = props.multiple ? event.dataTransfer.files : [event.dataTransfer.files[0]]

    checkFileType(actualFiles)

    if (actualFiles.length > 0) {
        files.value = [...files.value, ...actualFiles]
        uploadFiles()
    }
}

function inputFiles(event) {
    if (props.disabled) return
    event.preventDefault()
    dragging.value = false
    if (!event.target?.files?.length) return
    const actualFiles = props.multiple ? event.target.files : [event.target.files[0]]

    checkFileType(actualFiles)

    files.value = [...files.value, ...actualFiles]
    uploadFiles()
}

function uploadFiles() {
    if (props.disabled) return

    const promises = files.value.map(file => {
        return new Promise((resolve, reject) => {
            const formData = new FormData()
            formData.append('payload', JSON.stringify({
                visibility: props.visibility,
                folder: props.folder,
                organisation_id: props.organisationId
            }))
            formData.append('file', file)
            $lara.post($apiRoute('spa.documents.store'), {
                body: formData,
                formDataRequest: true
            }).then(response => {
                files.value.splice(files.value.indexOf(file), 1)
                resolve(response.data)
            }).catch(error => {
                // remove error file
                files.value.splice(files.value.indexOf(file), 1)
                notification.error($t('files.uploadError'), error.data?.message)
                reject(error)
            })
        })
    })

    Promise.all(promises).then((uploadedDocs) => {
        emits('update:modelValue', [...(props.modelValue || []), ...uploadedDocs])
    }).catch(error => {
        console.error(error)
    })
}

function checkFileType(files) {
    for (let i = 0; i < files.length; i++) {
        const file = files[i]
        let accept = props.accept

        if (accept.includes(',')) {
            accept = accept.split(',')
        } else if (accept.includes('|')) {
            accept = accept.split('|')
        } else {
            accept = [accept]
        }

        accept = accept.map((data) => data.trim())

        const fileType = file.type
        const fileExtension = file.name.slice(file.name.lastIndexOf('.'))
        const wildcardFileType = fileType.substring(0, fileType.indexOf("/")) + "/*"

        if (accept.length > 0 && !accept.includes(fileType) && !accept.includes(wildcardFileType) && !accept.includes(fileExtension)) {
            // delete files with wrong file type
            files.splice(i, 1)
            notification.error($t('files.wrongFileType', {fileType: props.accept}))
        }
    }
}
</script>

<style scoped>

</style>